/**
 * Vanilla particle sprites.
 *
 * Minecraft ships one small PNG per sprite in a frame sequence, and the two the
 * engine needs are exactly that:
 *   generic_0..7     the SMOKE puff: an 8x8 sprite drawn about 3x, one frame per
 *                    slice of its life — what a primed charge fizzes, one a tick
 *   explosion_0..15  the EXPLOSION poof: a 32x32 sprite that swells through its
 *                    six-to-ten tick life — what a blast cloud is made of
 *
 * Each sequence lives in its own sampler2DArray (the pattern render/fire.ts uses
 * for the flame sheets) because the two are different sizes; the shader picks
 * one by layer. Without the extracted PNGs (public/mc/particle/, see
 * scripts/extract-minecraft-textures.mjs) each frame is painted procedurally, so
 * the engine still runs on a bare checkout.
 */

import * as THREE from 'three';
import { mulberry32 } from '../noise';

export interface ParticleTextures {
  /** SMOKE frames, SMOKE_SIZE square, one layer per frame. */
  smoke: THREE.DataArrayTexture;
  smokeFrames: number;
  /** EXPLOSION frames, POOF_SIZE square, one layer per frame. */
  poof: THREE.DataArrayTexture;
  poofFrames: number;
}

/** Sprite edge lengths, vanilla's texel sizes. */
export const SMOKE_SIZE = 8;
export const POOF_SIZE = 32;
const SMOKE_FRAMES = 8;
const POOF_FRAMES = 16;

/** White puff with a soft edge — the shape both vanilla sequences share. */
function paintPuff(size: number, radius: number, shade: number, out: Uint8Array, at: number): void {
  const c = (size - 1) / 2;
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const d = Math.hypot(x - c, y - c) / radius;
      const alpha = d >= 1 ? 0 : Math.round(255 * (1 - d) ** 1.4);
      const o = at + (y * size + x) * 4;
      out[o] = shade;
      out[o + 1] = shade;
      out[o + 2] = shade;
      out[o + 3] = alpha;
    }
  }
}

/** Grey puff that grows over the sequence (no MC assets present). */
function proceduralSmoke(): Uint8Array {
  const data = new Uint8Array(SMOKE_FRAMES * SMOKE_SIZE * SMOKE_SIZE * 4);
  for (let f = 0; f < SMOKE_FRAMES; f++) {
    const t = (f + 1) / SMOKE_FRAMES;
    paintPuff(SMOKE_SIZE, 1.2 + t * 2.4, 190, data, f * SMOKE_SIZE * SMOKE_SIZE * 4);
  }
  return data;
}

/** Pale poof that swells and thins out over the sequence (no MC assets present). */
function proceduralPoof(): Uint8Array {
  const rnd = mulberry32(0x9e3779b9);
  const data = new Uint8Array(POOF_FRAMES * POOF_SIZE * POOF_SIZE * 4);
  for (let f = 0; f < POOF_FRAMES; f++) {
    const t = (f + 1) / POOF_FRAMES;
    const layer = f * POOF_SIZE * POOF_SIZE * 4;
    paintPuff(POOF_SIZE, 2 + t * 14, 235, data, layer);
    // a little internal structure, so a growing disc does not read as a bubble
    for (let i = 0; i < 3; i++) {
      const r = 4 + rnd() * 10;
      paintPuff(POOF_SIZE, r, 255, data, layer);
    }
  }
  return data;
}

async function loadImage(name: string): Promise<HTMLImageElement | null> {
  const img = new Image();
  img.src = `mc/particle/${name}.png`; // relative — works under vite's './' base
  try {
    await img.decode();
    return img;
  } catch {
    return null; // not extracted — procedural fallback
  }
}

/** One sprite (RGBA, resampled to `size` square).
 *
 *  Minecraft stores many of these as greyscale **alpha masks** rather than RGBA
 *  images (explosion_5 is a 1-bit mask, explosion_15 an 8-bit one): white means
 *  fully opaque, black fully transparent, and the sprite's colour is white.
 *  Those come out of the canvas as fully opaque pixels, so a sprite with no
 *  transparency anywhere is read as a mask — thresholding it instead would turn
 *  a soft puff into a solid brick. */
function decodeSprite(img: HTMLImageElement, size: number): Uint8Array | null {
  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx || img.naturalWidth === 0) return null;
  ctx.drawImage(img, 0, 0);
  const src = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
  let hasAlpha = false;
  for (let i = 3; i < src.length; i += 4) {
    if (src[i] !== 255) { hasAlpha = true; break; }
  }

  const out = new Uint8Array(size * size * 4);
  for (let y = 0; y < size; y++) {
    const sy = Math.min(canvas.height - 1, Math.floor(((y + 0.5) * canvas.height) / size));
    for (let x = 0; x < size; x++) {
      const sx = Math.min(canvas.width - 1, Math.floor(((x + 0.5) * canvas.width) / size));
      const s = (sy * canvas.width + sx) * 4;
      const o = (y * size + x) * 4;
      const r = src[s], g = src[s + 1], b = src[s + 2];
      if (hasAlpha) {
        out[o] = r;
        out[o + 1] = g;
        out[o + 2] = b;
        out[o + 3] = src[s + 3];
      } else {
        out[o] = 255;
        out[o + 1] = 255;
        out[o + 2] = 255;
        // the mask's own level is the coverage
        out[o + 3] = Math.round(0.299 * r + 0.587 * g + 0.114 * b);
      }
    }
  }
  return out;
}

function arrayTexture(data: Uint8Array, size: number, frames: number): THREE.DataArrayTexture {
  const texture = new THREE.DataArrayTexture(data, size, size, frames);
  texture.format = THREE.RGBAFormat;
  texture.type = THREE.UnsignedByteType;
  texture.colorSpace = THREE.SRGBColorSpace; // decoded on sample
  // clamp, like the flame sheets: a sprite maps uv 0..1, nothing to wrap
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  // vanilla draws particles unmipped — a mip level averages the sprite's soft
  // edge into the middle and turns a small puff into a grey blob
  texture.magFilter = THREE.NearestFilter;
  texture.minFilter = THREE.LinearFilter;
  texture.generateMipmaps = false;
  texture.needsUpdate = true;
  return texture;
}

export async function buildParticleTextures(): Promise<ParticleTextures> {
  const smokeNames = Array.from({ length: SMOKE_FRAMES }, (_, i) => `generic_${i}`);
  const poofNames = Array.from({ length: POOF_FRAMES }, (_, i) => `explosion_${i}`);
  const [smokeImgs, poofImgs] = await Promise.all([
    Promise.all(smokeNames.map(loadImage)),
    Promise.all(poofNames.map(loadImage)),
  ]);

  const fallbackSmoke = proceduralSmoke();
  const fallbackPoof = proceduralPoof();
  const smoke = new Uint8Array(SMOKE_FRAMES * SMOKE_SIZE * SMOKE_SIZE * 4);
  const poof = new Uint8Array(POOF_FRAMES * POOF_SIZE * POOF_SIZE * 4);
  const smokeLayer = SMOKE_SIZE * SMOKE_SIZE * 4;
  const poofLayer = POOF_SIZE * POOF_SIZE * 4;

  smokeImgs.forEach((img, i) => {
    const sprite = img && decodeSprite(img, SMOKE_SIZE);
    smoke.set(sprite ?? fallbackSmoke.subarray(i * smokeLayer, (i + 1) * smokeLayer), i * smokeLayer);
  });
  poofImgs.forEach((img, i) => {
    const sprite = img && decodeSprite(img, POOF_SIZE);
    poof.set(sprite ?? fallbackPoof.subarray(i * poofLayer, (i + 1) * poofLayer), i * poofLayer);
  });

  return {
    smoke: arrayTexture(smoke, SMOKE_SIZE, SMOKE_FRAMES),
    smokeFrames: SMOKE_FRAMES,
    poof: arrayTexture(poof, POOF_SIZE, POOF_FRAMES),
    poofFrames: POOF_FRAMES,
  };
}
